import type { ReactNode } from 'react';
import { fmtTokens } from '../pageUtils';
import { fmtGbp, fmtUsd, NOT_CHARGED_LABEL, type CaseCosts } from '../api/caseCosts';

const skel = <span className="skel" />;

/** A cost that was not incurred (mock or Dev mode, a reused result, a cached tile): what it would have been, struck through. */
function NotCharged({ children, why }: { children: ReactNode; why: string }) {
  return <s style={{ color: 'var(--ink-3)' }} title={`Not charged: ${why}`}>{children}</s>;
}

/** The value paid, followed by the struck-through estimate of what was not paid, when there is any. */
function paidAndNot(paid: string, notPaid: number, fmt: (n: number) => string, why: string): ReactNode {
  return notPaid > 0 ? <>{paid} <NotCharged why={why}>{fmt(notPaid)}</NotCharged></> : paid;
}

/** The case's cost at a glance, by the service that bills it: what was paid, and struck through what was not (cached, mock or
 * Dev mode, a reused result). */
export function CaseCostKpis({ costs, loading }: { costs: CaseCosts | null; loading: boolean }) {
  const show = (v: ReactNode) => (loading || !costs ? skel : v);
  const paid = (service: string) => costs?.byServiceUsd[service] ?? 0;
  const notPaid = (service: string) => costs?.notChargedByServiceUsd[service] ?? 0;
  const calls = (service: string) => costs?.rows.filter(r => r.service === service).reduce((n, r) => n + r.calls, 0) ?? 0;
  const assessCalls = costs?.rows.filter(r => r.service === 'Bedrock' && r.phase === 'Assessment' && r.step === 'Analysis').reduce((n, r) => n + r.calls, 0) ?? 0;
  const why = 'cached, mock or Dev mode, or a reused result';
  const requests = (n: number, what: string) => (n ? <><b>{n.toLocaleString()}</b> {what}</> : 'None');
  return (
    <div className="kpis" aria-label="What this case has cost">
      <div className="kpi">
        <span className="kpi-label">Tokens</span>
        <span className="kpi-value">{show(String(Math.max(0, (costs?.tokensSpent ?? 0) - (costs?.tokensRefunded ?? 0))))}</span>
        <span className="kpi-sub">
          {costs?.tokensSpent
            ? <><b>{costs.tokensSpent}</b> spent{costs.tokensRefunded ? <>, <b>{costs.tokensRefunded}</b> refunded for failed runs</> : null}</>
            : 'None spent'}
        </span>
      </div>
      <div className="kpi">
        <span className="kpi-label">Bedrock</span>
        <span className="kpi-value">{show(paidAndNot(fmtUsd(paid('Bedrock')), notPaid('Bedrock'), fmtUsd, why))}</span>
        <span className="kpi-sub">
          {costs?.unpricedCalls
            ? <b style={{ color: 'var(--warn)' }}>{costs.unpricedCalls} call{costs.unpricedCalls === 1 ? '' : 's'} on an unpriced model</b>
            : assessCalls ? <>Model calls; <b>{assessCalls}</b> assessment run{assessCalls === 1 ? '' : 's'}</> : 'Model calls'}
        </span>
      </div>
      <div className="kpi">
        <span className="kpi-label">Amazon Location</span>
        <span className="kpi-value">{show(paidAndNot(fmtUsd(paid('Amazon Location')), notPaid('Amazon Location'), fmtUsd, why))}</span>
        <span className="kpi-sub">{requests(calls('Amazon Location'), 'address lookups')}</span>
      </div>
      <div className="kpi">
        <span className="kpi-label">Textract</span>
        <span className="kpi-value">{show(paidAndNot(fmtUsd(paid('Textract')), notPaid('Textract'), fmtUsd, why))}</span>
        <span className="kpi-sub">{requests(calls('Textract'), 'pages read from plans')}</span>
      </div>
      <div className="kpi">
        <span className="kpi-label">OS map</span>
        <span className="kpi-value">{show(paidAndNot(fmtGbp(costs?.osMapGbp ?? 0), costs?.osMapCachedGbp ?? 0, fmtGbp, 'tiles served from the cache'))}</span>
        <span className="kpi-sub">
          {costs?.osMapTiles || costs?.osMapCachedTiles
            ? <>
              <b>{(costs.osMapTiles ?? 0).toLocaleString()}</b> premium tiles, before the free allowance
              {costs.osMapCachedTiles ? <>; <b>{costs.osMapCachedTiles.toLocaleString()}</b> from the cache</> : null}
            </>
            : 'No premium tiles'}
        </span>
      </div>
    </div>
  );
}

/** Every metered model call, one row per phase, step and model, with the OS map and the totals. */
export function CaseCostBreakdown({ costs }: { costs: CaseCosts }) {
  const charged = costs.rows.filter(r => !r.notCharged);
  return (
    <table className="tbl tbl-list">
      <thead>
        <tr>
          <th>Phase</th>
          <th>Step</th>
          <th className="hide-mobile">Service</th>
          <th className="num hide-mobile">Calls</th>
          <th className="num hide-mobile">Tokens in</th>
          <th className="num hide-mobile">Tokens out</th>
          <th className="num">Cost</th>
        </tr>
      </thead>
      <tbody>
        {costs.rows.map(r => {
          const cost = r.unpricedCalls === r.calls ? <span style={{ color: 'var(--warn)' }}>Unpriced</span> : fmtUsd(r.costUsd);
          return (
            <tr key={`${r.service}|${r.phase}|${r.step}|${r.model}|${r.notCharged ?? ''}`}>
              <td>{r.phase}</td>
              <td>
                {r.step}
                {r.notCharged && <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 11.5 }}>Not charged: {NOT_CHARGED_LABEL[r.notCharged]}</span>}
              </td>
              <td className="hide-mobile" style={{ fontSize: 11.5 }}>{r.service}<span className="mono" style={{ display: 'block', color: 'var(--ink-3)' }}>{r.model}</span></td>
              <td className="num mono hide-mobile">{r.calls}</td>
              <td className="num mono hide-mobile">{r.service === 'Bedrock' ? fmtTokens(r.inputTokens) : '—'}</td>
              <td className="num mono hide-mobile">{r.service === 'Bedrock' ? fmtTokens(r.outputTokens) : '—'}</td>
              <td className="num mono">
                {r.notCharged ? <NotCharged why={NOT_CHARGED_LABEL[r.notCharged]}>{cost}</NotCharged> : cost}
                {r.unpricedCalls > 0 && r.unpricedCalls < r.calls && <span style={{ color: 'var(--warn)' }}> + {r.unpricedCalls} unpriced</span>}
              </td>
            </tr>
          );
        })}
        {costs.osMapTiles > 0 && (
          <tr>
            <td>OS map</td>
            <td>{costs.osMapTiles.toLocaleString()} premium tiles</td>
            <td className="hide-mobile" style={{ fontSize: 11.5 }}>Ordnance Survey<span className="mono" style={{ display: 'block', color: 'var(--ink-3)' }}>OS Maps API</span></td>
            <td className="num mono hide-mobile">—</td>
            <td className="num mono hide-mobile">—</td>
            <td className="num mono hide-mobile">—</td>
            <td className="num mono">{fmtGbp(costs.osMapGbp)}</td>
          </tr>
        )}
        {costs.osMapCachedTiles > 0 && (
          <tr>
            <td>OS map</td>
            <td>
              {costs.osMapCachedTiles.toLocaleString()} premium tiles from the cache
              <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 11.5 }}>Not charged: already fetched</span>
            </td>
            <td className="hide-mobile" style={{ fontSize: 11.5 }}>Ordnance Survey<span className="mono" style={{ display: 'block', color: 'var(--ink-3)' }}>OS Maps API</span></td>
            <td className="num mono hide-mobile">—</td>
            <td className="num mono hide-mobile">—</td>
            <td className="num mono hide-mobile">—</td>
            <td className="num mono"><NotCharged why="tiles served from the cache">{fmtGbp(costs.osMapCachedGbp)}</NotCharged></td>
          </tr>
        )}
        <tr style={{ fontWeight: 600 }}>
          <td colSpan={2}>Total</td>
          <td className="hide-mobile" />
          <td className="num mono hide-mobile">{charged.reduce((n, r) => n + r.calls, 0)}</td>
          <td className="num mono hide-mobile">{fmtTokens(charged.reduce((n, r) => n + r.inputTokens, 0))}</td>
          <td className="num mono hide-mobile">{fmtTokens(charged.reduce((n, r) => n + r.outputTokens, 0))}</td>
          <td className="num mono">
            {fmtUsd(costs.modelUsd)}{costs.osMapGbp > 0 ? ` + ${fmtGbp(costs.osMapGbp)}` : ''}
            {(costs.notChargedUsd > 0 || costs.osMapCachedGbp > 0) && (
              <span style={{ display: 'block', fontWeight: 400 }}>
                <NotCharged why="not incurred">
                  {fmtUsd(costs.notChargedUsd)}{costs.osMapCachedGbp > 0 ? ` + ${fmtGbp(costs.osMapCachedGbp)}` : ''}
                </NotCharged>
              </span>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
